import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import "./index.css";

type Operator = "+" | "-" | "×" | "÷" | null;

export function App() {
  const [display, setDisplay] = useState("0");
  const [previousValue, setPreviousValue] = useState<number | null>(null);
  const [operator, setOperator] = useState<Operator>(null);
  const [waitingForOperand, setWaitingForOperand] = useState(false);
  const [lastOperator, setLastOperator] = useState<Operator>(null);
  const [lastOperand, setLastOperand] = useState<number | null>(null);

  const inputDigit = (digit: string) => {
    if (waitingForOperand) {
      setDisplay(digit);
      setWaitingForOperand(false);
    } else {
      setDisplay(display === "0" ? digit : display + digit);
    }
  };

  const inputDecimal = () => {
    if (waitingForOperand) {
      setDisplay("0.");
      setWaitingForOperand(false);
      return;
    }
    if (!display.includes(".")) {
      setDisplay(display + ".");
    }
  };

  const clear = () => {
    setDisplay("0");
    setPreviousValue(null);
    setOperator(null);
    setWaitingForOperand(false);
    setLastOperator(null);
    setLastOperand(null);
  };

  const toggleSign = () => {
    const value = parseFloat(display);
    if (value !== 0) {
      setDisplay(String(-value));
    }
  };

  const inputPercent = () => {
    const value = parseFloat(display);
    setDisplay(String(value / 100));
  };

  const calculate = (left: number, right: number, op: Operator): number => {
    switch (op) {
      case "+": return left + right;
      case "-": return left - right;
      case "×": return left * right;
      case "÷": return right !== 0 ? left / right : 0;
      default: return right;
    }
  };

  const handleOperator = (nextOperator: Operator) => {
    const inputValue = parseFloat(display);

    if (previousValue !== null && operator && !waitingForOperand) {
      const result = calculate(previousValue, inputValue, operator);
      const resultStr = parseFloat(result.toFixed(10)).toString();
      setDisplay(resultStr);
      setPreviousValue(result);
    } else {
      setPreviousValue(inputValue);
    }

    setOperator(nextOperator);
    setWaitingForOperand(true);
    setLastOperator(null);
    setLastOperand(null);
  };

  const handleEquals = () => {
    const currentValue = parseFloat(display);

    if (previousValue !== null && operator !== null) {
      // First press of = after an operation
      const result = calculate(previousValue, currentValue, operator);
      const resultStr = parseFloat(result.toFixed(10)).toString();
      setDisplay(resultStr);
      setLastOperator(operator);
      setLastOperand(currentValue);
      setPreviousValue(null);
      setOperator(null);
      setWaitingForOperand(true);
    } else if (lastOperator !== null && lastOperand !== null) {
      // Repeated press of = : re-apply last operation
      const result = calculate(currentValue, lastOperand, lastOperator);
      const resultStr = parseFloat(result.toFixed(10)).toString();
      setDisplay(resultStr);
      setWaitingForOperand(true);
    }
  };

  const formatDisplay = (value: string): string => {
    if (value.includes(".") && !value.endsWith(".")) {
      const [intPart, decPart] = value.split(".");
      const num = parseInt(intPart, 10);
      return num.toLocaleString("en-US") + "." + decPart;
    }
    if (value.endsWith(".")) {
      const num = parseInt(value, 10);
      return num.toLocaleString("en-US") + ".";
    }
    const num = parseFloat(value);
    if (Number.isInteger(num) && !value.includes(".")) {
      return num.toLocaleString("en-US");
    }
    return value;
  };

  const displayFontSize = display.length > 12 ? "text-2xl" : display.length > 8 ? "text-3xl" : "text-5xl";

  const operatorButtons: { label: string; op: Operator }[] = [
    { label: "÷", op: "÷" },
    { label: "×", op: "×" },
    { label: "−", op: "-" },
    { label: "+", op: "+" },
  ];

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-white via-zinc-100 to-zinc-200 p-4">
      <Card className="w-full max-w-xs bg-zinc-900/80 backdrop-blur-xl border-zinc-800 shadow-2xl rounded-3xl overflow-hidden">
        <CardContent className="p-0">
          {/* Display */}
          <div className="px-6 pt-8 pb-4 min-h-[120px] flex flex-col justify-end items-end">
            {operator && previousValue !== null && (
              <div className="text-zinc-500 text-sm mb-1 font-mono">
                {previousValue.toLocaleString("en-US")} {operator}
              </div>
            )}
            <div className={`${displayFontSize} font-light text-white tracking-tight font-mono truncate max-w-full`}>
              {formatDisplay(display)}
            </div>
          </div>

          {/* Buttons */}
          <div className="grid grid-cols-4 gap-[1px] bg-zinc-800/50 p-[1px]">
            {/* Row 1: AC, +/-, %, ÷ */}
            <Button
              variant="secondary"
              className="h-16 rounded-none bg-zinc-700 hover:bg-zinc-600 text-white text-lg font-medium border-0"
              onClick={clear}
            >
              {display === "0" && previousValue === null ? "AC" : "C"}
            </Button>
            <Button
              variant="secondary"
              className="h-16 rounded-none bg-zinc-700 hover:bg-zinc-600 text-white text-lg font-medium border-0"
              onClick={toggleSign}
            >
              +/−
            </Button>
            <Button
              variant="secondary"
              className="h-16 rounded-none bg-zinc-700 hover:bg-zinc-600 text-white text-lg font-medium border-0"
              onClick={inputPercent}
            >
              %
            </Button>
            <Button
              className={`h-16 rounded-none text-white text-xl font-medium border-0 ${
                operator === "÷" && waitingForOperand
                  ? "bg-white text-yellow-500 hover:bg-zinc-100"
                  : "bg-yellow-500 hover:bg-yellow-400"
              }`}
              onClick={() => handleOperator("÷")}
            >
              ÷
            </Button>

            {/* Row 2: 7, 8, 9, × */}
            {["7", "8", "9"].map((d) => (
              <Button
                key={d}
                variant="secondary"
                className="h-16 rounded-none bg-zinc-800 hover:bg-zinc-700 text-white text-xl font-medium border-0"
                onClick={() => inputDigit(d)}
              >
                {d}
              </Button>
            ))}
            <Button
              className={`h-16 rounded-none text-white text-xl font-medium border-0 ${
                operator === "×" && waitingForOperand
                  ? "bg-white text-yellow-500 hover:bg-zinc-100"
                  : "bg-yellow-500 hover:bg-yellow-400"
              }`}
              onClick={() => handleOperator("×")}
            >
              ×
            </Button>

            {/* Row 3: 4, 5, 6, - */}
            {["4", "5", "6"].map((d) => (
              <Button
                key={d}
                variant="secondary"
                className="h-16 rounded-none bg-zinc-800 hover:bg-zinc-700 text-white text-xl font-medium border-0"
                onClick={() => inputDigit(d)}
              >
                {d}
              </Button>
            ))}
            <Button
              className={`h-16 rounded-none text-white text-xl font-medium border-0 ${
                operator === "-" && waitingForOperand
                  ? "bg-white text-yellow-500 hover:bg-zinc-100"
                  : "bg-yellow-500 hover:bg-yellow-400"
              }`}
              onClick={() => handleOperator("-")}
            >
              −
            </Button>

            {/* Row 4: 1, 2, 3, + */}
            {["1", "2", "3"].map((d) => (
              <Button
                key={d}
                variant="secondary"
                className="h-16 rounded-none bg-zinc-800 hover:bg-zinc-700 text-white text-xl font-medium border-0"
                onClick={() => inputDigit(d)}
              >
                {d}
              </Button>
            ))}
            <Button
              className={`h-16 rounded-none text-white text-xl font-medium border-0 ${
                operator === "+" && waitingForOperand
                  ? "bg-white text-yellow-500 hover:bg-zinc-100"
                  : "bg-yellow-500 hover:bg-yellow-400"
              }`}
              onClick={() => handleOperator("+")}
            >
              +
            </Button>

            {/* Row 5: 0 (span 2), ., = */}
            <Button
              variant="secondary"
              className="h-16 rounded-none bg-zinc-800 hover:bg-zinc-700 text-white text-xl font-medium border-0 col-span-2"
              onClick={() => inputDigit("0")}
            >
              0
            </Button>
            <Button
              variant="secondary"
              className="h-16 rounded-none bg-zinc-800 hover:bg-zinc-700 text-white text-xl font-medium border-0"
              onClick={inputDecimal}
            >
              .
            </Button>
            <Button
              className="h-16 rounded-none bg-yellow-500 hover:bg-yellow-400 text-white text-xl font-medium border-0"
              onClick={handleEquals}
            >
              =
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}

export default App;
